import type Phaser from "phaser";
import { CANVAS_COLOURS, rectHandlePoints, type PendingRect, type StrokeCells } from "daedalus-core";

/**
 * A cell stroke drawn as it paints, and the pending rectangle with its handles (ADR-054's amendment of
 * 2026-10-01, decisions 2 and 3). A zone or collision stroke's covered cells are filled over the map in the
 * colour they will take while the button is down; each draw adds only the cells covered since the last one, a
 * run of cells covered one after another along a row as one rectangle, and starts again only when the stroke's
 * span was replaced or the colour changed. The pending rectangle is filled the same way and outlined in the cursor's colour with
 * a square handle at each corner and the middle of each edge, a constant size on screen at any zoom.
 */

/**
 * The fill's opacity over the map.
 */
export const STROKE_PREVIEW_ALPHA = 0.5;

/**
 * A handle's side in screen pixels.
 */
export const RECT_HANDLE_PIXELS = 8;

const OUTLINE = CANVAS_COLOURS.cursor;
const HANDLE_EDGE = CANVAS_COLOURS.selectedPropOutline;

/**
 * The drawing for one scene.
 */
export class StrokePreview {
  private readonly fill: Phaser.GameObjects.Graphics;
  private readonly frame: Phaser.GameObjects.Graphics;
  private source: StrokeCells | null = null;
  private drawn = 0;
  private restarts = 0;
  private colour = -1;
  private shownRect: PendingRect | null = null;
  private shownUnit = 0;
  private shownColour = -1;
  private shownZoom = 0;

  /**
   * @param scene The scene.
   * @param cellSize One cell of the stroke's grid in world pixels.
   * @param depth The drawing's depth, above the map and its overlays.
   */
  public constructor(
    scene: Phaser.Scene,
    private readonly cellSize: number,
    depth: number,
  ) {
    this.fill = scene.add.graphics().setDepth(depth);
    this.frame = scene.add.graphics().setDepth(depth + 1);
  }

  /**
   * How many cells the stroke drawing shows.
   *
   * @returns The count.
   */
  public get cellCount(): number {
    return this.source === null ? 0 : this.drawn;
  }

  /**
   * Draws a stroke's cells: the ones covered since the last draw, or all of them after a restart or a change
   * of stroke or colour.
   *
   * @param stroke The stroke's cells.
   * @param gridWidth The stroke's grid width in cells.
   * @param colour The fill colour.
   */
  public draw(stroke: StrokeCells, gridWidth: number, colour: number): void {
    const { fill, cellSize } = this;
    if (stroke !== this.source || stroke.restarts !== this.restarts || colour !== this.colour) {
      fill.clear();
      this.source = stroke;
      this.restarts = stroke.restarts;
      this.colour = colour;
      this.drawn = 0;
    }
    const cells = stroke.cells;
    if (this.drawn === cells.length) {
      return;
    }
    fill.fillStyle(colour, STROKE_PREVIEW_ALPHA);
    let i = this.drawn;
    while (i < cells.length) {
      const index = cells[i] as number;
      const x = index % gridWidth;
      let run = 1;
      while (i + run < cells.length && cells[i + run] === index + run && x + run < gridWidth) {
        run += 1;
      }
      fill.fillRect(x * cellSize, ((index - x) / gridWidth) * cellSize, run * cellSize, cellSize);
      i += run;
    }
    this.drawn = cells.length;
  }

  /**
   * Removes the stroke drawing.
   */
  public clear(): void {
    if (this.source !== null) {
      this.fill.clear();
      this.source = null;
      this.drawn = 0;
    }
  }

  /**
   * Draws the pending rectangle, redrawing only when it, its colour or the zoom changed.
   *
   * @param rect The rectangle in its own units.
   * @param unit One unit in world pixels.
   * @param colour The fill colour.
   * @param zoom The camera's zoom, which keeps the outline and handles a constant size on screen.
   */
  public showRect(rect: PendingRect, unit: number, colour: number, zoom: number): void {
    const shown = this.shownRect;
    if (shown !== null && shown.x0 === rect.x0 && shown.y0 === rect.y0 && shown.x1 === rect.x1 && shown.y1 === rect.y1 && unit === this.shownUnit && colour === this.shownColour && zoom === this.shownZoom) {
      return;
    }
    this.shownRect = rect;
    this.shownUnit = unit;
    this.shownColour = colour;
    this.shownZoom = zoom;
    const { frame } = this;
    const x = rect.x0 * unit;
    const y = rect.y0 * unit;
    const w = (rect.x1 - rect.x0 + 1) * unit;
    const h = (rect.y1 - rect.y0 + 1) * unit;
    const line = 1 / zoom;
    const side = RECT_HANDLE_PIXELS / zoom;
    frame.clear();
    frame.fillStyle(colour, STROKE_PREVIEW_ALPHA);
    frame.fillRect(x, y, w, h);
    frame.lineStyle(2 * line, OUTLINE.rgb, OUTLINE.alpha);
    frame.strokeRect(x, y, w, h);
    for (const point of rectHandlePoints(rect, unit)) {
      frame.fillStyle(OUTLINE.rgb, OUTLINE.alpha);
      frame.fillRect(point.x - side / 2, point.y - side / 2, side, side);
      frame.lineStyle(line, HANDLE_EDGE.rgb, HANDLE_EDGE.alpha);
      frame.strokeRect(point.x - side / 2, point.y - side / 2, side, side);
    }
  }

  /**
   * Removes the pending rectangle's drawing.
   */
  public hideRect(): void {
    if (this.shownRect !== null) {
      this.frame.clear();
      this.shownRect = null;
    }
  }

  /**
   * Removes the drawing from the scene.
   */
  public destroy(): void {
    this.fill.destroy();
    this.frame.destroy();
  }
}
